import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Skeleton from '@components/common/skeleton';
import { showToast } from '@components/toast';
import {
    apiMerchantConfigService,
    MerchantConfig,
    MerchantConfigApiError
} from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import DevPageHeader from '../_shared/PageHeader';
import styles from './styles.module.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every((item) => setB.has(item));
}

function validateDomainEntry(value: string, existing: string[]): string {
    if (!value) return 'Domain cannot be empty.';
    if (value.includes('://')) {
        return 'Enter a bare hostname without a scheme (e.g. shop.example.com).';
    }
    if (value.includes('/')) return 'Enter a bare hostname without a path.';
    if (value.includes(' ')) return 'Domain cannot contain spaces.';
    if (existing.includes(value)) return 'This domain is already in the list.';
    return '';
}

// ─── Main Layout ──────────────────────────────────────────────────────────────

const DevelopersSettingsLayout = () => {
    const { user, businessData } = useUserContext();
    const { env } = useEnvironmentContext();

    // ── All state declarations ────────────────────────────────────────────────
    const [config, setConfig] = useState<MerchantConfig | null>(null);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [configUnavailable, setConfigUnavailable] = useState(false);

    const [domains, setDomains] = useState<string[]>([]);
    const [domainInput, setDomainInput] = useState('');
    const [domainInputError, setDomainInputError] = useState('');
    const [isSavingDomains, setIsSavingDomains] = useState(false);
    const [domainsError, setDomainsError] = useState('');
    const [domainsSuccess, setDomainsSuccess] = useState(false);

    // ── Config load ────────────────────────────────────────────────────────────
    // Generation counter — guards against a stale environment's GET (e.g. a
    // 'test' fetch still in flight when the pill switches to 'live')
    // resolving after a newer one and repopulating the form from the wrong
    // environment. Only the most recent loadConfig call may apply its result.
    const loadConfigGenerationRef = useRef(0);

    const loadConfig = useCallback(async () => {
        const generation = ++loadConfigGenerationRef.current;
        setIsLoadingConfig(true);
        setConfigUnavailable(false);
        try {
            const c = await apiMerchantConfigService.get(env);
            if (generation !== loadConfigGenerationRef.current) return;
            setConfig(c);
            setDomains(c.allowed_domains);
        } catch {
            if (generation !== loadConfigGenerationRef.current) return;
            setConfigUnavailable(true);
        } finally {
            if (generation === loadConfigGenerationRef.current) {
                setIsLoadingConfig(false);
            }
        }
    }, [env]);

    useEffect(() => {
        void loadConfig();
    }, [loadConfig]);

    // ── Computed dirty flags ───────────────────────────────────────────────────
    const domainsAreDirty = config !== null && !setsEqual(domains, config.allowed_domains);

    // ── Domain handlers ───────────────────────────────────────────────────────
    const handleAddDomain = () => {
        const v = domainInput.trim().toLowerCase();
        const err = validateDomainEntry(v, domains);
        if (err) {
            setDomainInputError(err);
            return;
        }
        setDomainInputError('');
        setDomains((prev) => [...prev, v]);
        setDomainInput('');
        setDomainsSuccess(false);
    };

    const handleRemoveDomain = (domain: string) => {
        setDomains((prev) => prev.filter((d) => d !== domain));
        setDomainsSuccess(false);
    };

    const handleSaveDomains = async () => {
        setDomainsError('');
        setDomainsSuccess(false);
        setIsSavingDomains(true);
        try {
            const updated = await apiMerchantConfigService.update(
                { allowed_domains: [...domains] },
                env
            );
            setConfig(updated);
            setDomains(updated.allowed_domains);
            setDomainsSuccess(true);
            showToast.success('Allowed domains updated.');
        } catch (err) {
            if (err instanceof MerchantConfigApiError && err.status === 400) {
                setDomainsError(err.message);
            } else {
                showToast.error("Couldn't save allowed domains. Please try again.");
            }
        } finally {
            setIsSavingDomains(false);
        }
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <DevPageHeader
                title="Settings"
                subtitle="Manage your developer profile and checkout allowlists."
            />

            <div className={styles.card}>
                <div className={styles.card_header}>
                    <div className={styles.icon_circle}>
                        <Icon name="business" width={22} height={22} />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body2" weight="semibold">
                            Merchant profile
                        </Typography>
                        <p className={styles.description}>
                            Your business identity used in checkouts and webhooks
                        </p>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.field}>
                        <p className={styles.label}>Company name</p>
                        <Typography variant="body2" className={styles.value}>
                            {businessData?.company_name || '—'}
                        </Typography>
                    </div>
                    <div className={styles.field}>
                        <p className={styles.label}>Account email</p>
                        <Typography variant="body2" className={styles.value}>
                            {user?.email || '—'}
                        </Typography>
                    </div>
                </div>

                {isLoadingConfig && (
                    <div className={styles.spinner_wrapper}>
                        <Skeleton className={styles.spinner_skeleton} />
                    </div>
                )}

                {!isLoadingConfig && configUnavailable && (
                    <div className={styles.unavailable_notice}>
                        Configuration editing is temporarily unavailable — some settings could not
                        be loaded from the server.
                    </div>
                )}

                {!isLoadingConfig && !configUnavailable && (
                    <div className={styles.edit_section}>
                        <div className={styles.field}>
                            <p className={styles.label}>Allowed domains</p>
                            <p className={styles.field_hint}>
                                Only these domains may host your XEPPT checkout button / receive
                                results.
                            </p>
                            {domains.length > 0 && (
                                <div className={styles.domain_list}>
                                    {domains.map((d) => (
                                        <span key={d} className={styles.domain_chip}>
                                            {d}
                                            <button
                                                className={styles.domain_chip_remove}
                                                onClick={() => handleRemoveDomain(d)}
                                                disabled={isSavingDomains}
                                                title={`Remove ${d}`}>
                                                <Icon name="close" width={12} height={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className={styles.domain_add_row}>
                                <input
                                    type="text"
                                    value={domainInput}
                                    placeholder="shop.example.com"
                                    disabled={isSavingDomains}
                                    onChange={(e) => {
                                        setDomainInput(e.target.value);
                                        setDomainInputError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddDomain();
                                    }}
                                    className={
                                        domainInputError
                                            ? `${styles.edit_input} ${styles.input_error}`
                                            : styles.edit_input
                                    }
                                />
                                <Button
                                    variant="outlined-primary"
                                    size="small"
                                    onClick={handleAddDomain}
                                    disabled={isSavingDomains}>
                                    Add
                                </Button>
                            </div>
                            {domainInputError && (
                                <span className={styles.field_error}>{domainInputError}</span>
                            )}
                            {domainsError && (
                                <span className={styles.field_error}>{domainsError}</span>
                            )}
                        </div>
                        <div className={styles.save_row}>
                            {domainsSuccess && (
                                <span className={styles.field_success}>Saved.</span>
                            )}
                            {domains.length === 0 && (
                                <span className={styles.domain_clear_warning}>
                                    An empty list blocks all checkout origins.
                                </span>
                            )}
                            <Button
                                variant="gold"
                                size="small"
                                onClick={() => void handleSaveDomains()}
                                disabled={!domainsAreDirty || isSavingDomains}>
                                {isSavingDomains ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default DevelopersSettingsLayout;
